import { useOutletContext } from 'react-router-dom';
import { useFeaturesContext } from '../providers/FeaturesProvider';

/**
 * useSubscription — access the current user's subscription details and status.
 *
 * Checks:
 * 1. FeaturesProvider context (holds { features, subscription })
 * 2. React Router OutletContext (passed down by DashboardLayout)
 * 3. localStorage fallback ('subscription' or user.subscription)
 *
 * @returns {{
 *   subscription: object | null,
 *   isActive: boolean,
 *   isExpired: boolean,
 *   isLoaded: boolean,
 * }}
 */
export function useSubscription() {
  const context = useFeaturesContext();
  const outletContext = useOutletContext();

  let subscription =
    context && typeof context === 'object' && 'subscription' in context
      ? context.subscription
      : null;

  // React Router OutletContext fallback if mounted inside an Outlet
  if (!subscription && outletContext?.subscription) {
    subscription = outletContext.subscription;
  }

  // LocalStorage fallback
  if (!subscription) {
    try {
      const stored = localStorage.getItem('subscription');
      if (stored) {
        subscription = JSON.parse(stored);
      } else {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          if (userObj?.subscription) {
            subscription = userObj.subscription;
          }
        }
      }
    } catch (e) {
      console.error('Failed to parse subscription from localStorage', e);
    }
  }

  const isLoaded =
    subscription !== null ||
    (context !== null &&
      typeof context === 'object' &&
      'subscription' in context);

  const isActive = subscription?.is_active === true;
  const isExpired = subscription !== null && subscription?.is_active === false;

  return {
    subscription,
    isActive,
    isExpired,
    isLoaded,
  };
}
